import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting, TPredicateMode } from '@feathr/blackbox';
import { AUDIENCE_TARGETING_KINDS, CampaignClass, TargetableClass } from '@feathr/blackbox';
import {
  Alert,
  AsyncDisplay,
  Button,
  CardV2 as Card,
  ContextMenu,
  EmptyState,
  Heading,
  SegmentedControl,
  Well,
} from '@feathr/components';
import Glue from '@feathr/extender/components/Glue';
import { AgeDemographics, Interests, Locations } from '@feathr/extender/components/Targetings';
import { SessionCacheProvider, useCampaignChannelVisibility } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';
import type { IUseAudienceBreakdown } from '@feathr/report_components';

import { type IUniqueEmailsResult } from '../AddWizard.useReconcileUniqueEmails';
import BelowThresholdAlert from './AdWizardAudienceStep.BelowThresholdAlert';
import { useText } from './AdWizardAudienceStep.useText';
import {
  getAffinityAudienceSize,
  getReachShortfall,
  MIN_REACHABLE_AUDIENCE,
} from './AdWizardAudienceStep.utils';
import AffinityTargeting from './AffinityTargeting';
import EmailListTargeting from './EmailListTargeting';
import EmailListUploadButton from './EmailListTargeting/EmailListUploadButton';
import GeoFenceTargeting from './GeoFenceTargeting';
import LookalikeAudienceTargeting from './LookalikeAudienceTargeting';
import LookalikeTargeting from './LookalikeTargeting';
import SearchKeywordTargeting from './SearchKeywordTargeting';
import SeedSegmentTargeting from './SeedSegmentTargeting';
import SegmentTargeting from './SegmentTargeting';

import * as styles from './AdWizardAudienceStep.css';

interface IProps {
  campaign: Campaign;
  targetingsList: ListResponse<Targeting>;
  emailValidation: IUniqueEmailsResult;
  /** The shared audience breakdown computed once at the wizard level. */
  audienceData: IUseAudienceBreakdown;
}

export function getTargetingKind(campaignClass: CampaignClass): string {
  const targetingKinds: Partial<Record<CampaignClass, string>> = {
    [CampaignClass.Segment]: 'segment',
    [CampaignClass.Lookalike]: 'lookalike',
    [CampaignClass.SeedSegment]: 'lookalike',
    [CampaignClass.Affinity]: 'lookalike',
    // Email list upload button specifies the email_list kind.
    // This allows the use of segments on the same page.
    [CampaignClass.EmailList]: 'segment',
    [CampaignClass.EmailListFacebook]: 'segment',
    [CampaignClass.Search]: 'search',
    [CampaignClass.MobileGeoFencing]: 'geofence',
    [CampaignClass.MobileGeoFenceRetargeting]: 'geo_audience',
    [CampaignClass.Facebook]: 'segment',
    [CampaignClass.Omni]: 'segment',
  };

  return targetingKinds[campaignClass] ?? 'segment';
}

const NUM_RECOMMENDED_EMAILS_FACEBOOK = 10000;

function AdWizardAudienceStepContent({
  campaign,
  targetingsList,
  emailValidation,
  audienceData,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetings, Targetables } = useStore();
  const { showMeta, showDisplay } = useCampaignChannelVisibility(campaign);
  const { numTargetedReachableUsers } = audienceData;
  const cls = campaign.get('_cls');
  const targetingKind = getTargetingKind(cls);
  const localUrl = useLocalUrl();
  const { description, helpDeskUrl } = useText({ cls });
  const targetings = targetingsList.models;

  const {
    isGeofencingCampaign,
    isSegmentCampaign,
    isEmailMappingCampaign,
    isDisabledCampaignState,
    isAffinityCampaign,
    isFacebook,
    isOmniCampaign,
    isOmniReachCampaign,
  } = campaign;
  const isDisabledCampaignClass = isGeofencingCampaign || isEmailMappingCampaign;
  // Default to match_any for meta
  const mode = campaign.get('mode') ?? 'match_any';
  const {
    numUniqueEmails,
    hasEnoughUniqueEmails,
    remainingEmails,
    isLoading: isLoadingNumUniqueEmails,
  } = emailValidation;

  const removeTargeting = useCallback(
    (targeting: Targeting) => {
      if (targeting.isEphemeral) {
        targeting.collection!.remove(targeting.id);
        targetings.remove(targeting);
      } else {
        targeting.set({ is_archived: true });
      }
    },
    [targetings],
  );

  const onClick = useCallback(() => {
    const model = Targetings.create({
      parent: campaign.get('id'),
      kind: targetingKind,
      included: true,
      group: mode === 'match_all' ? Date.now() : 0,
    });

    runInAction(() => {
      targetings.push(model);
    });
  }, [targetings, mode, campaign, cls, Targetings]);

  const searchTargetings = targetings.filter((t) => {
    return t.get('kind') === 'search' && !t.get('is_archived');
  });

  const filteredTargetings = targetings.filter(
    (t) => !t.get('is_archived') && AUDIENCE_TARGETING_KINDS.includes(t.get('kind')),
  );

  const isReconcilingEmails = isEmailMappingCampaign && isLoadingNumUniqueEmails;
  const hasEmptyTargetings = filteredTargetings.length === 0;

  const numTotalTargeted = isEmailMappingCampaign
    ? numUniqueEmails ?? 0
    : numTargetedReachableUsers ?? 0;
  const formattedNumTotalTargeted = numeral(numTotalTargeted).format('0,0');
  const labelTotalTargeted = isEmailMappingCampaign
    ? t('Total unique email addresses')
    : t('Total reachable people');

  const addTargetButton = (
    <Button
      disabled={
        (isDisabledCampaignState && isDisabledCampaignClass) ||
        (campaign.get('_cls') === CampaignClass.Search && searchTargetings.length >= 1) ||
        isReconcilingEmails
      }
      key={'add'}
      name={'add_target'}
      onClick={onClick}
      prefix={getIconForAction('add')}
    >
      {t('Add target')}
    </Button>
  );

  const handleChangeAudienceGroup = useCallback(
    (value?: string) => {
      if (!value) {
        return;
      }
      campaign.set({ mode: value as TPredicateMode });
      // Update all existing targetings when mode changes
      runInAction(() => {
        filteredTargetings.forEach((targeting, index) => {
          if (value === 'match_all') {
            targeting.set({ group: Date.now() + index });
          } else {
            targeting.set({ group: 0 });
          }
        });
      });
    },
    [filteredTargetings],
  );

  const addTargetButtonEmailMapping = (
    <ContextMenu
      buttonText={t('Add target')}
      disabled={isReconcilingEmails}
      position={'bottom-start'}
      suffix={getIconForAction('arrowDown')}
    >
      <EmailListUploadButton
        campaign={campaign}
        onRemove={removeTargeting}
        targetings={targetings}
      />
      {!isFacebook && <ContextMenu.Item onClick={onClick}>{t('Select a Group')}</ContextMenu.Item>}
    </ContextMenu>
  );

  const targetButton = isEmailMappingCampaign ? addTargetButtonEmailMapping : addTargetButton;
  const isNotReconcilingEmails = !isLoadingNumUniqueEmails && numUniqueEmails;
  const showModeSegmentedControl = (isAffinityCampaign || isSegmentCampaign) && !isFacebook;

  // Pixl Plus, Retargeting or email mapping
  const isRetargetingOrEmailMappingCampaign =
    isEmailMappingCampaign || (isSegmentCampaign && !isOmniCampaign);

  const isLoading = audienceData.isLoading;

  /*
   * The 2,000 floor is enforced per channel (Display and Meta each), so the alerts use per-channel
   * reach and mirror the gate in validateStepTargets via getReachShortfall.
   */
  const displayReachableUsers =
    numTargetedReachableUsers - (showMeta ? audienceData.numTargetedMeta ?? 0 : 0);
  const { displayShort: isDisplayShort, metaShort: isMetaShort } = getReachShortfall({
    displayReach: displayReachableUsers,
    metaReach: audienceData.numTargetedMeta ?? 0,
    appliesToDisplay: isOmniCampaign ? showDisplay : true,
    appliesToMeta: isOmniCampaign ? showMeta : false,
    isLoading: false,
  });

  // Retargeting only; email mapping has its own unique-email alert. Suppressed while the
  // breakdown is loading so the alert doesn't flash against a not-yet-computed (0) reach.
  const showSmallAudienceAlert =
    isSegmentCampaign &&
    !isOmniCampaign &&
    !isFacebook &&
    !hasEmptyTargetings &&
    !isLoading &&
    isDisplayShort;

  // Before a channel is chosen (integrations empty) neither channel shortfall applies; fall back to
  // the total reachable audience (displayReachableUsers holds it when no Meta is subtracted).
  const noChannelSelected = isOmniCampaign && !showDisplay && !showMeta;
  const isTotalReachShort = noChannelSelected && displayReachableUsers < MIN_REACHABLE_AUDIENCE;
  const showOmniSmallAudienceAlert =
    isOmniCampaign &&
    !hasEmptyTargetings &&
    !isLoading &&
    (isDisplayShort || isMetaShort || isTotalReachShort);

  const reachMinimum = numeral(MIN_REACHABLE_AUDIENCE).format('0,0');
  const omniServesMeta = isOmniCampaign && showMeta;
  // Name the short channel(s) when the campaign serves on Meta; otherwise use the generic copy.
  let omniSmallAudienceDescription = t(
    'Pixl Plus campaigns require a reachable audience of at least {{minimum}} people to launch. Broaden your targeting or import more contacts to continue.',
    { minimum: reachMinimum },
  );
  if (isDisplayShort && isMetaShort) {
    omniSmallAudienceDescription = t(
      'Your Display and Meta audiences must each be able to reach at least {{minimum}} people before this campaign can be published. Broaden your targeting or import more contacts to continue.',
      { minimum: reachMinimum },
    );
  } else if (isMetaShort) {
    omniSmallAudienceDescription = t(
      'Your Meta audience must be able to reach at least {{minimum}} people before this campaign can be published. Broaden your targeting or import more contacts to continue.',
      { minimum: reachMinimum },
    );
  } else if (omniServesMeta) {
    omniSmallAudienceDescription = t(
      'Your Display audience must be able to reach at least {{minimum}} people before this campaign can be published. Broaden your targeting or import more contacts to continue.',
      { minimum: reachMinimum },
    );
  }

  const smallAudienceDescription = t(
    'Your target audience must be able to reach at least {{minimum}} people before this campaign can be published. Campaigns with smaller audiences may not spend their full budget effectively, as ad campaigns perform better with larger audiences. Combine multiple groups or broaden your targeting to continue.',
    { minimum: reachMinimum },
  );
  const affinitySmallAudienceDescription = t(
    'Your target audience must include at least {{minimum}} people before this campaign can be published. Campaigns with smaller audiences may not spend their full budget effectively, as ad campaigns perform better with larger audiences. Choose a broader affinity audience or add more to continue.',
    { minimum: reachMinimum },
  );

  // Affinity reach is the sum of the selected audiences' `unique_user_count`.
  const affinityTargetables = isAffinityCampaign
    ? filteredTargetings
        .filter((targeting) => !!targeting.get('target_data'))
        .map((targeting) =>
          Targetables.get(targeting.get('target_data')!, { _cls: TargetableClass.affinity }),
        )
    : [];
  // Suppressed while any selected affinity targetable is still loading its person count.
  const showAffinitySmallAudienceAlert =
    isAffinityCampaign &&
    affinityTargetables.length > 0 &&
    !affinityTargetables.some((targetable) => targetable.isPending) &&
    getAffinityAudienceSize(affinityTargetables) < MIN_REACHABLE_AUDIENCE;

  const targetsCardWidth = isSegmentCampaign || isAffinityCampaign ? 'wide' : 'narrow';

  return (
    <>
      {isOmniReachCampaign ? (
        <>
          <Locations campaign={campaign} targetings={targetings} />
          <AgeDemographics campaign={campaign} targetings={targetings} />
          <Interests campaign={campaign} targetings={targetings} />
        </>
      ) : (
        <>
          <Card
            contentClassName={isOmniCampaign ? styles.left : ''}
            name={'targeting_groups_card'}
            // Targets with groups need extra space to build predicates
            width={isOmniCampaign ? 'full' : targetsCardWidth}
          >
            <Card.Header
              description={
                <>
                  {description}
                  {helpDeskUrl}
                </>
              }
              title={t('People')}
            />
            <Card.Content addVerticalGap={true}>
              {showModeSegmentedControl && (
                <SegmentedControl
                  data={[
                    { label: t('Any of the following'), value: 'match_any' },
                    { label: t('All of the following'), value: 'match_all' },
                  ]}
                  fullWidth={false}
                  name={'mode'}
                  onChange={handleChangeAudienceGroup}
                  value={mode}
                />
              )}

              {isGeofencingCampaign && (
                <Alert
                  description={t(
                    'Geofence locations within the EU are no longer available due to GDPR.',
                  )}
                  title={t('EU geofence locations unavailable')}
                  type={'warning'}
                >
                  <a
                    href={
                      'https://help.feathr.co/hc/en-us/articles/360039271953-Data-Protection-Compliance-with-Feathr'
                    }
                    target={'_blank'}
                  >
                    {t('Learn more about GDPR Compliance')}
                  </a>
                </Alert>
              )}
              {filteredTargetings.map((targeting: Targeting, index: number): JSX.Element => {
                if (isSegmentCampaign) {
                  const isLast = index === filteredTargetings.length - 1;
                  return (
                    <React.Fragment key={targeting.listId}>
                      <SegmentTargeting
                        campaign={campaign}
                        key={targeting.listId}
                        onRemove={removeTargeting}
                        showFiltersInModal={true}
                        targeting={targeting}
                        targetings={targetings}
                      />
                      <Glue isLastItem={isLast} mode={mode} suppressGap={true} t={t} />
                    </React.Fragment>
                  );
                }
                if (cls === CampaignClass.Lookalike) {
                  return (
                    <LookalikeTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (cls === CampaignClass.SeedSegment) {
                  return (
                    <SeedSegmentTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (cls === CampaignClass.Affinity) {
                  const isLast = index === filteredTargetings.length - 1;
                  return (
                    <React.Fragment key={targeting.listId}>
                      <AffinityTargeting
                        campaign={campaign}
                        onRemove={removeTargeting}
                        targeting={targeting}
                      />
                      <Glue isLastItem={isLast} mode={mode} suppressGap={true} t={t} />
                    </React.Fragment>
                  );
                }
                if (isEmailMappingCampaign) {
                  return (
                    <React.Fragment key={targeting.listId}>
                      {targeting.get('kind') === 'email_list' && (
                        <EmailListTargeting
                          campaign={campaign}
                          isReconcilingEmails={isLoadingNumUniqueEmails}
                          onRemove={removeTargeting}
                          targeting={targeting}
                        />
                      )}
                      {targeting.get('kind') === 'segment' && (
                        <SegmentTargeting
                          campaign={campaign}
                          isReconcilingEmails={isLoadingNumUniqueEmails}
                          onRemove={removeTargeting}
                          showFiltersInModal={true}
                          targeting={targeting}
                          targetings={targetings}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                if (cls === CampaignClass.Search) {
                  return (
                    <SearchKeywordTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (isGeofencingCampaign) {
                  return (
                    <GeoFenceTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                throw new Error('Unexpected campaign class in AdWizardAudienceStep');
              })}

              {isNotReconcilingEmails && !hasEnoughUniqueEmails ? (
                <Alert
                  description={t(
                    'Add at least {{num}} more unique email addresses to proceed. Campaigns with smaller audiences may not spend their full budget effectively, as ad campaigns perform better with larger audiences.',
                    {
                      count: remainingEmails,
                      num: numeral(remainingEmails).format('0,0'),
                    },
                  )}
                  name={'email_minimum_alert'}
                  title={t('{{num}} unique email addresses have been added.', {
                    count: numUniqueEmails,
                    num: numeral(numUniqueEmails).format('0,0'),
                  })}
                  type={'danger'}
                />
              ) : (
                isFacebook &&
                hasEnoughUniqueEmails &&
                numUniqueEmails &&
                numUniqueEmails < NUM_RECOMMENDED_EMAILS_FACEBOOK && (
                  <Alert
                    description={t(
                      'Feathr recommends {{emailCountRecommendation}} unique email address to ensure good results from the email mapping process.',
                      {
                        count: numUniqueEmails,
                        emailCountRecommendation: numeral(NUM_RECOMMENDED_EMAILS_FACEBOOK).format(
                          '0,0',
                        ),
                      },
                    )}
                    title={t(
                      'This campaign may not perform well with {{emailCountProvided}} unique email addresses.',
                      {
                        count: numUniqueEmails,
                        emailCountProvided: numeral(numUniqueEmails).format('0,0'),
                      },
                    )}
                    type={'info'}
                  />
                )
              )}

              <BelowThresholdAlert
                description={omniSmallAudienceDescription}
                name={'target_more_alert'}
                show={showOmniSmallAudienceAlert}
              >
                <a href={localUrl('data/imports')} target={'_blank'}>
                  {t('Import more contacts')}
                </a>
              </BelowThresholdAlert>

              <BelowThresholdAlert
                description={smallAudienceDescription}
                name={'small_audience_alert'}
                show={showSmallAudienceAlert}
              />

              <BelowThresholdAlert
                description={affinitySmallAudienceDescription}
                name={'affinity_small_audience_alert'}
                show={showAffinitySmallAudienceAlert}
              />

              {hasEmptyTargetings ? (
                <EmptyState
                  description={t('Add a target to get started')}
                  label={t('No targets added')}
                  theme={'neutral'}
                >
                  {targetButton}
                </EmptyState>
              ) : (
                targetButton
              )}

              {(isRetargetingOrEmailMappingCampaign || isOmniCampaign) && (
                <Well label={labelTotalTargeted} name={'total_targeted'}>
                  <AsyncDisplay
                    isLoading={isLoading}
                    // Match the line height of the heading
                    skeleton={{ options: { paragraphs: 1, height: 28, width: 100 } }}
                  >
                    <Heading mantineComponent={'span'} name={'total_targeted_value'} variant={'h4'}>
                      {/* Force reachable people to 0 so we don't have to call breakdown again for 0 data  */}
                      {filteredTargetings.length !== 0 ? formattedNumTotalTargeted : 0}
                    </Heading>
                  </AsyncDisplay>
                </Well>
              )}
            </Card.Content>
          </Card>
          {isOmniCampaign && (
            <SessionCacheProvider>
              <LookalikeAudienceTargeting
                campaign={campaign}
                isLoading={isLoading}
                numReachableUsers={filteredTargetings.length !== 0 ? numTotalTargeted : 0}
                targetings={targetings}
              />
            </SessionCacheProvider>
          )}
        </>
      )}
    </>
  );
}

export default observer(AdWizardAudienceStepContent);
